// src/utils/exportPendenzen.js
// Excel-Export der Pendenzenliste (US-PEND-003). Baut ein exceljs-Workbook aus den
// (bereits gefilterten) Pendenzen auf und löst Verantwortliche aus projectPersons auf.
import ExcelJS from "exceljs";
import { richTextToPlainText } from "./richText";

// Label-Maps (gespiegelt zu Pendenzen.jsx – bewusst lokal, damit der Export
// eigenständig nutzbar ist).
const ART_LABELS = { P: "Pendenz", E: "Entscheid", N: "Notiz" };
const KATEGORIE_LABELS = {
  Org: "Organisation",
  Pl: "Planung",
  Ko: "Kosten",
  Te: "Termine",
  Div: "Diverses",
};
const STATUS_LABELS = {
  offen: "Offen",
  inBearbeitung: "In Bearbeitung",
  kritisch: "Kritisch",
  erledigt: "Erledigt",
};

// ISO-Datum -> "DD.MM.YYYY"; leere/ungültige Werte -> "" (leere Zelle)
const formatDateDE = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

// Heutiges Datum als "YYYY-MM-DD" (lokal) für den Dateinamen
const todayIso = () => {
  const d = new Date();
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d - tzOffset).toISOString().slice(0, 10);
};

// Spaltendefinitionen (Reihenfolge + Breite + deutsche Titel)
const COLUMNS = [
  { header: "Nr", width: 6, get: (p) => p.nummer },
  { header: "Art", width: 12, get: (p) => ART_LABELS[p.typ] || p.typ },
  {
    header: "Kategorie",
    width: 14,
    get: (p) => KATEGORIE_LABELS[p.kategorie] || p.kategorie,
  },
  { header: "Themen", width: 22, get: (p) => (p.themen || []).join(", ") },
  { header: "Beschreibung", width: 50, get: (p) => richTextToPlainText(p.beschreibung) },
  {
    header: "Verantwortlich",
    width: 24,
    // Anzeigename(n) werden beim Speichern in `verantwortlich` abgelegt (MF /
    // Organisationen / Team), siehe Pendenzen.jsx.
    get: (p) => p.verantwortlich || "",
  },
  { header: "Deadline", width: 13, get: (p) => formatDateDE(p.terminSoll) },
  { header: "neue Deadline", width: 13, get: (p) => formatDateDE(p.terminNeu) },
  {
    header: "Status",
    width: 12,
    get: (p) => STATUS_LABELS[p.status] || p.status,
  },
  { header: "Erledigt am", width: 13, get: (p) => formatDateDE(p.erledigtAm) },
  { header: "Kommentar", width: 40, get: (p) => p.kommentar || "" },
];

/**
 * Exportiert die übergebenen (gefilterten) Pendenzen als .xlsx und löst den Download aus.
 * @param {Object} params
 * @param {Array}  params.pendenzen   Bereits gefilterte/gesuchte Einträge.
 * @param {String} params.projectCode Projektnummer für den Dateinamen.
 */
export const exportPendenzenToExcel = async ({
  pendenzen,
  projectCode = "",
}) => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("Pendenzen");

  // Kopfzeile
  ws.columns = COLUMNS.map((c) => ({ header: c.header, width: c.width }));
  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: "middle" };

  // Datenzeilen
  pendenzen.forEach((p) => {
    ws.addRow(COLUMNS.map((c) => c.get(p)));
  });

  // Beschreibung/Kommentar umbrechen
  const wrapCols = ["Beschreibung", "Kommentar"];
  COLUMNS.forEach((c, idx) => {
    if (wrapCols.includes(c.header)) {
      ws.getColumn(idx + 1).alignment = { wrapText: true, vertical: "top" };
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Pendenzen_${projectCode || "Projekt"}_${todayIso()}.xlsx`;
  a.click();
  window.URL.revokeObjectURL(url);
};
